import { useState, FormEvent, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signUp, Kid } from '../auth/cognito';

// Phone helper — Cognito requires E.164 (+15551234567).
function normalizePhone(raw: string): string {
  const digits = raw.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return digits;
  if (digits.length === 10) return `+1${digits}`; // assume NA if 10 digits
  return `+${digits}`;
}

type KidForm = { name: string; yearOfBirth: string };

// Involvement types correspond 1:1 with the three landing-page "Join" cards.
// The landing page passes ?role=parent|coach|partner so the radio defaults
// to the user's intent, but they can still change it here.
export type Role = 'parent' | 'coach' | 'partner';
const ROLES: { value: Role; label: string }[] = [
  { value: 'parent', label: 'Parent' },
  { value: 'coach', label: 'Coach / Advisor' },
  { value: 'partner', label: 'Partner' },
];

function parseRole(raw: string | null): Role {
  return raw === 'coach' || raw === 'partner' ? raw : 'parent';
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = useMemo(() => parseRole(searchParams.get('role')), [searchParams]);

  const [role, setRole] = useState<Role>(initialRole);
  const [parentName, setParentName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [kids, setKids] = useState<KidForm[]>([{ name: '', yearOfBirth: '' }]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isParent = role === 'parent';

  const updateKid = (i: number, patch: Partial<KidForm>) =>
    setKids((ks) => ks.map((k, idx) => (idx === i ? { ...k, ...patch } : k)));

  const addKid = () => setKids((ks) => [...ks, { name: '', yearOfBirth: '' }]);
  const removeKid = (i: number) => setKids((ks) => ks.filter((_, idx) => idx !== i));

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    let cleanedKids: Kid[] = [];
    if (isParent) {
      for (const k of kids) {
        if (!k.name.trim() && !k.yearOfBirth.trim()) continue; // skip blank rows
        const yob = parseInt(k.yearOfBirth, 10);
        const currentYear = new Date().getFullYear();
        if (!k.name.trim() || !yob || yob < 1990 || yob > currentYear) {
          setError(`Please enter a valid name and year of birth (1990–${currentYear}) for each kid.`);
          return;
        }
        cleanedKids.push({ name: k.name.trim(), yearOfBirth: yob });
      }
      if (cleanedKids.length === 0) {
        setError('Please add at least one kid.');
        return;
      }
    }
    if (password.length < 10) {
      setError('Password must be at least 10 characters with upper, lower, and a number.');
      return;
    }

    setSubmitting(true);
    try {
      const phoneE164 = normalizePhone(phone);
      await signUp({ email, password, parentName, phoneNumber: phoneE164 });

      // Stash form data so the verify step can post the profile after confirmation.
      sessionStorage.setItem(
        'mk_pending_profile',
        JSON.stringify({ role, parentName, phoneNumber: phoneE164, kids: cleanedKids, password, email }),
      );
      navigate('/verify', { state: { email } });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign up failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const nameLabel = isParent ? 'Your full name' : 'Your full name';
  const lede = isParent
    ? "Parent signup. We'll verify your email to keep the community trusted."
    : role === 'coach'
      ? "Coach / advisor signup. We'll verify your email to keep the community trusted."
      : "Partner signup. We'll verify your email to keep the community trusted.";

  return (
    <div className="auth-wrap">
      <form className="auth-card" onSubmit={handleSubmit} noValidate>
        <h1>Create your account</h1>
        <p className="lede">{lede}</p>

        {error && <div className="error-banner">{error}</div>}

        <fieldset className="role-group" aria-labelledby="role-legend">
          <legend id="role-legend">I'm joining as a…</legend>
          <div className="role-options">
            {ROLES.map((r) => (
              <label key={r.value} className={`role-option ${role === r.value ? 'is-selected' : ''}`}>
                <input
                  type="radio"
                  name="role"
                  value={r.value}
                  checked={role === r.value}
                  onChange={() => setRole(r.value)}
                />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="field">
          <label htmlFor="parentName">{nameLabel}</label>
          <input
            id="parentName" type="text" required autoComplete="name"
            value={parentName} onChange={(e) => setParentName(e.target.value)}
          />
        </div>

        <div className="row-2">
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email" type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="phone">Phone number</label>
            <input
              id="phone" type="tel" required autoComplete="tel"
              placeholder="+1 416 555 1234"
              value={phone} onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password" type="password" required minLength={10} autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
          <span className="hint">At least 10 characters with uppercase, lowercase, and a number.</span>
        </div>

        {isParent && (
          <>
            <h2 className="section-label">Your kid(s)</h2>

            {kids.map((k, i) => (
              <div key={i} className="kid-block">
                <div className="kid-head">
                  <strong>Kid {i + 1}</strong>
                  {kids.length > 1 && (
                    <button type="button" onClick={() => removeKid(i)}>Remove</button>
                  )}
                </div>
                <div className="row-2">
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label htmlFor={`kid-name-${i}`}>Name</label>
                    <input
                      id={`kid-name-${i}`} type="text"
                      value={k.name} onChange={(e) => updateKid(i, { name: e.target.value })}
                    />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label htmlFor={`kid-yob-${i}`}>Year of birth</label>
                    <input
                      id={`kid-yob-${i}`} type="number" min={1990} max={new Date().getFullYear()}
                      placeholder="2012"
                      value={k.yearOfBirth} onChange={(e) => updateKid(i, { yearOfBirth: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button type="button" className="add-kid-btn" onClick={addKid}>+ Add another kid</button>
          </>
        )}

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Creating account…' : 'Create account'}
        </button>

        <div className="switch">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
